import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Alert, Spinner, Badge, Form, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faSearch, faFilter, faUser, faFilm, faMusic, faBoxOpen, faStar, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function WishlistManagement() {
    const [items, setItems] = useState([]);
    const [statistiche, setStatistiche] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filtri
    const [filtroTipo, setFiltroTipo] = useState('');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');

    // Paginazione
    const [page, setPage] = useState(1);
    const [pageSize] = useState(25);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        fetchItems();
    }, [page, filtroTipo, search]);

    useEffect(() => {
        fetchStatistiche();
    }, []);

    const fetchItems = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('access_token');
            let url = `${API_URL}/api/wishlist/admin/tutte?page=${page}&page_size=${pageSize}`;
            if (filtroTipo) url += `&tipo=${filtroTipo}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setItems(response.data.items);
            setTotal(response.data.total);
        } catch (err) {
            setError('Errore nel caricamento delle wishlist');
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistiche = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`${API_URL}/api/wishlist/admin/statistiche`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStatistiche(response.data);
        } catch (err) {
            console.error('Errore statistiche wishlist:', err);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setSearch(searchInput);
        setPage(1);
    };

    const handleFiltroTipo = (tipo) => {
        setFiltroTipo(tipo);
        setPage(1);
    };

    const tipoIcon = (tipo) => {
        if (tipo === 'film') return <FontAwesomeIcon icon={faFilm} className="text-primary me-1" />;
        if (tipo === 'musica') return <FontAwesomeIcon icon={faMusic} className="text-success me-1" />;
        return <FontAwesomeIcon icon={faBoxOpen} className="text-warning me-1" />;
    };

    const tipoBadge = (tipo) => {
        const variants = { film: 'primary', musica: 'success', collectible: 'warning' };
        return <Badge bg={variants[tipo] || 'secondary'}>{tipo}</Badge>;
    };

    const totalPages = Math.ceil(total / pageSize);

    return (
        <div>
            <h2 className="mb-4">
                <FontAwesomeIcon icon={faHeart} className="text-danger me-2" />
                Wishlist Clienti
            </h2>

            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

            <Tabs defaultActiveKey="lista" className="mb-4">
                {/* Tab Lista */}
                <Tab eventKey="lista" title={<><FontAwesomeIcon icon={faFilter} className="me-1" />Lista</>}>
                    <Card className="mb-3">
                        <Card.Body>
                            <Row className="g-2 align-items-end">
                                <Col xs={12} md={5}>
                                    <Form onSubmit={handleSearch}>
                                        <Form.Label className="fw-semibold">Cerca cliente</Form.Label>
                                        <div className="d-flex gap-2">
                                            <Form.Control
                                                placeholder="Email cliente..."
                                                value={searchInput}
                                                onChange={e => setSearchInput(e.target.value)}
                                            />
                                            <Button type="submit" variant="outline-primary">
                                                <FontAwesomeIcon icon={faSearch} />
                                            </Button>
                                        </div>
                                    </Form>
                                </Col>
                                <Col xs={12} md={4}>
                                    <Form.Label className="fw-semibold">Tipo prodotto</Form.Label>
                                    <div className="d-flex gap-2">
                                        {['', 'film', 'musica', 'collectible'].map(t => (
                                            <Button
                                                key={t || 'all'}
                                                size="sm"
                                                variant={filtroTipo === t ? 'primary' : 'outline-secondary'}
                                                onClick={() => handleFiltroTipo(t)}
                                            >
                                                {t || 'Tutti'}
                                            </Button>
                                        ))}
                                    </div>
                                </Col>
                                <Col xs={12} md={3} className="text-muted small d-flex align-items-end">
                                    {total} elementi totali
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    ) : (
                        <>
                            <Card>
                                <Table responsive hover className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Prodotto</th>
                                            <th>Tipo</th>
                                            <th>Prezzo</th>
                                            <th>Disponibile</th>
                                            <th>Cliente</th>
                                            <th>Aggiunto il</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.length === 0 ? (
                                            <tr><td colSpan={6} className="text-center text-muted py-4">Nessun elemento trovato</td></tr>
                                        ) : items.map(item => (
                                            <tr key={item.id}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {item.immagine_url && (
                                                            <img
                                                                src={item.immagine_url.startsWith('http') ? item.immagine_url : `${process.env.REACT_APP_STATIC_FILES_BASE_URL}${item.immagine_url}`}
                                                                alt=""
                                                                style={{ width: 36, height: 50, objectFit: 'cover', borderRadius: 3 }}
                                                            />
                                                        )}
                                                        <span className="fw-semibold">{item.titolo || `#${item.prodotto_id}`}</span>
                                                    </div>
                                                </td>
                                                <td>{tipoBadge(item.tipo)}</td>
                                                <td>{item.prezzo != null ? `€ ${Number(item.prezzo).toFixed(2)}` : '—'}</td>
                                                <td>
                                                    {item.disponibile == null ? '—' : item.disponibile
                                                        ? <Badge bg="success">Sì</Badge>
                                                        : <Badge bg="secondary">No</Badge>
                                                    }
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-1">
                                                        <FontAwesomeIcon icon={faUser} className="text-muted" size="sm" />
                                                        {item.cliente_id ? (
                                                            <Link to={`/clienti/${item.cliente_id}`} className="text-decoration-none">
                                                                {item.cliente_nome || item.cliente_email}
                                                                <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-1 text-muted" size="xs" />
                                                            </Link>
                                                        ) : (
                                                            <span className="text-muted">{item.cliente_email}</span>
                                                        )}
                                                    </div>
                                                    <small className="text-muted d-block">{item.cliente_email}</small>
                                                </td>
                                                <td className="text-muted small">
                                                    {item.created_at ? new Date(item.created_at).toLocaleDateString('it-IT') : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card>

                            {/* Paginazione */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                                    <Button variant="outline-secondary" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                                        &laquo; Prec
                                    </Button>
                                    <span className="text-muted small">Pagina {page} di {totalPages}</span>
                                    <Button variant="outline-secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                                        Succ &raquo;
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </Tab>

                {/* Tab Statistiche */}
                <Tab eventKey="statistiche" title={<><FontAwesomeIcon icon={faStar} className="me-1" />Più Desiderati</>}>
                    {!statistiche ? (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    ) : (
                        <>
                            <Row className="g-3 mb-4">
                                <Col xs={6} md={3}>
                                    <Card className="text-center border-danger">
                                        <Card.Body>
                                            <div className="fs-2 fw-bold text-danger">{statistiche.totale_items}</div>
                                            <div className="text-muted small">Elementi in wishlist</div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col xs={6} md={3}>
                                    <Card className="text-center border-primary">
                                        <Card.Body>
                                            <div className="fs-2 fw-bold text-primary">{statistiche.utenti_con_wishlist}</div>
                                            <div className="text-muted small">Clienti con wishlist</div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            <Card>
                                <Card.Header className="fw-semibold">
                                    <FontAwesomeIcon icon={faHeart} className="text-danger me-2" />
                                    Top 20 prodotti più desiderati
                                </Card.Header>
                                <Table responsive hover className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Prodotto</th>
                                            <th>Tipo</th>
                                            <th>Prezzo</th>
                                            <th>Disponibile</th>
                                            <th>Nelle wishlist</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {statistiche.top_prodotti.map((p, i) => (
                                            <tr key={`${p.tipo}-${p.prodotto_id}`}>
                                                <td className="text-muted fw-bold">{i + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {p.immagine_url && (
                                                            <img
                                                                src={p.immagine_url.startsWith('http') ? p.immagine_url : `${process.env.REACT_APP_STATIC_FILES_BASE_URL}${p.immagine_url}`}
                                                                alt=""
                                                                style={{ width: 36, height: 50, objectFit: 'cover', borderRadius: 3 }}
                                                            />
                                                        )}
                                                        <span className="fw-semibold">{p.titolo || `#${p.prodotto_id}`}</span>
                                                    </div>
                                                </td>
                                                <td>{tipoBadge(p.tipo)}</td>
                                                <td>{p.prezzo != null ? `€ ${Number(p.prezzo).toFixed(2)}` : '—'}</td>
                                                <td>
                                                    {p.disponibile == null ? '—' : p.disponibile
                                                        ? <Badge bg="success">Sì</Badge>
                                                        : <Badge bg="secondary">No</Badge>
                                                    }
                                                </td>
                                                <td>
                                                    <Badge bg="danger" pill className="px-3 py-2 fs-6">
                                                        <FontAwesomeIcon icon={faHeart} className="me-1" />
                                                        {p.count}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card>
                        </>
                    )}
                </Tab>
            </Tabs>
        </div>
    );
}
